
import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { HashRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, X, Globe, ChevronRight, BarChart3, ShieldCheck, Zap, 
  Settings, Users, Mail, Linkedin, MapPin, CheckCircle2, 
  ChevronDown, Bell, Thermometer, Activity, Cpu, 
  Pipette, Flame, Factory, Truck, Eye, Battery, Gauge, ArrowLeft, Phone, User, Loader2,
  Rocket, Target, Award, Lightbulb, AlertTriangle, Info, MessageSquare,
  LayoutDashboard, FileText, Database, PieChart, LogOut
} from 'lucide-react';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { 
  ResponsiveContainer, AreaChart, Area, CartesianGrid, Tooltip, XAxis, YAxis
} from 'recharts';
import { GoogleGenAI, Type } from "@google/genai";
import { translations } from './translations';
import { Language } from './types';

// Context for Language and Dark Mode
const AppContext = createContext<{
  lang: Language;
  setLang: (l: Language) => void;
  isDark: boolean;
  setIsDark: (d: boolean) => void;
  t: (key: string) => string;
}>({
  lang: 'fr',
  setLang: () => {},
  isDark: false,
  setIsDark: () => {},
  t: () => '',
});

const useAppContext = () => useContext(AppContext);

// --- Custom Logo Component ---
const LogoIcon = ({ className = "w-10 h-10" }: { className?: string }) => (
  <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    {/* Shield Shape */}
    <path d="M50 10C50 10 20 15 15 45C15 75 50 90 50 90C50 90 85 75 85 45C80 15 50 10 50 10Z" className="stroke-brand-dark dark:stroke-brand-primary" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
    
    {/* Gear Shape */}
    <path d="M50 35C41.7 35 35 41.7 35 50C35 58.3 41.7 65 50 65C58.3 65 65 58.3 65 50C65 41.7 58.3 35 50 35Z" className="fill-brand-primary" />
    <path d="M50 28V32M50 68V72M28 50H32M68 50H72M34.4 34.4L37.2 37.2M62.8 62.8L65.6 65.6M34.4 65.6L37.2 62.8M62.8 34.4L65.6 37.2" className="stroke-brand-primary" strokeWidth="6" strokeLinecap="round"/>
    
    {/* Circuit Lines */}
    <path d="M65 40L80 25" className="stroke-brand-primary" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="82" cy="23" r="3" className="fill-brand-primary" />
    <path d="M70 50L85 50" className="stroke-brand-primary" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="87" cy="50" r="3" className="fill-brand-primary" />
    <path d="M65 60L80 75" className="stroke-brand-primary" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="82" cy="77" r="3" className="fill-brand-primary" />
  </svg>
);

// --- Anonymous Team Avatar ---
const TeamAvatar = ({ className = "w-32 h-32" }: { className?: string }) => (
  <div className={`${className} bg-brand-light dark:bg-brand-dark/40 flex items-center justify-center relative overflow-hidden rounded-2xl border-4 border-white dark:border-slate-700`}>
    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-primary via-transparent to-transparent"></div>
    <User className="w-2/3 h-2/3 text-brand-dark/20 dark:text-brand-primary/20" strokeWidth={1.5} />
  </div>
);

// --- Scroll To Top Utility ---
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    } else {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [pathname, hash]);
  
  return null;
};

// --- Components ---

const Navbar = () => {
  const { lang, setLang, t, isDark, setIsDark } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('navHome'), href: "/" },
    { name: t('navAbout'), href: "/#about" },
    { name: t('navSolutions'), href: "/#solutions" },
    { name: t('navDashboard'), href: "/#dashboard" },
    { name: t('navTeam'), href: "/#team" },
    { name: t('navPricing'), href: "/#pricing" },
  ];

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    setIsOpen(false);
    if (href.startsWith('/#')) {
      const id = href.replace('/#', '');
      if (location.pathname === '/') {
        e.preventDefault();
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/90 dark:bg-slate-950/90 backdrop-blur-md shadow-sm border-b border-brand-light dark:border-slate-800' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3">
            <LogoIcon className="w-12 h-12" />
            <div className="flex flex-col">
              <span className="font-extrabold text-xl tracking-tighter text-brand-dark dark:text-white leading-none">SAFE PREDICTIVE</span>
              <span className="text-[11px] uppercase tracking-[0.2em] text-brand-primary font-bold">Innovation</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-8">
            {navLinks.map((link) => (
              <Link 
                key={link.name} 
                to={link.href} 
                onClick={(e) => handleLinkClick(e, link.href)}
                className="text-[13px] font-bold hover:text-brand-primary transition-colors uppercase tracking-widest"
              >
                {link.name}
              </Link>
            ))}
            <Link 
              to="/#contact" 
              onClick={(e) => handleLinkClick(e, "/#contact")}
              className="bg-brand-dark text-white px-6 py-2.5 rounded-lg hover:bg-brand-primary transition-all shadow-md text-xs font-black uppercase tracking-widest"
            >
              {t('navContact')}
            </Link>
            
            <div className="flex items-center space-x-4 ml-4 pl-4 border-l border-slate-200 dark:border-slate-800">
              <button 
                onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
                className="flex items-center space-x-1 text-sm font-bold hover:text-brand-primary transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span>{lang.toUpperCase()}</span>
              </button>
              <button onClick={() => setIsDark(!isDark)} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-xl">
                {isDark ? '🌙' : '☀️'}
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            <button onClick={() => setIsDark(!isDark)} className="p-2 text-xl">{isDark ? '🌙' : '☀️'}</button>
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-slate-600 dark:text-slate-400">
              {isOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white dark:bg-slate-900 border-b border-brand-light dark:border-slate-800 overflow-hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-4">
              {navLinks.map((link) => (
                <Link 
                  key={link.name} 
                  to={link.href} 
                  onClick={(e) => handleLinkClick(e, link.href)} 
                  className="block px-3 py-2 rounded-md hover:bg-brand-light dark:hover:bg-slate-800 font-bold uppercase text-sm"
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/#contact" onClick={(e) => handleLinkClick(e, "/#contact")} className="block px-3 py-2 text-brand-primary font-bold uppercase text-sm">{t('navContact')}</Link>
              <button 
                onClick={() => { setLang(lang === 'fr' ? 'en' : 'fr'); setIsOpen(false); }}
                className="w-full text-left px-3 py-2 flex items-center space-x-2 font-bold uppercase text-sm"
              >
                <Globe className="w-4 h-4" />
                <span>{lang === 'fr' ? 'English Version' : 'Version Française'}</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const AboutUs = () => {
  const { t } = useAppContext();
  return (
    <section id="about" className="py-24 bg-white dark:bg-slate-950 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start mb-24">
          <motion.div 
            initial={{ opacity: 0, x: -30 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-brand-light dark:bg-brand-dark/30 text-brand-dark dark:text-brand-primary text-sm font-bold mb-6">
              <Award className="w-4 h-4" />
              <span>{t('aboutPioneer')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-8 text-brand-dark dark:text-white">{t('aboutMainTitle')}</h2>
            <p className="text-xl text-slate-600 dark:text-slate-400 leading-relaxed mb-8">
              {t('aboutMainText')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="p-6 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
                <Target className="w-8 h-8 text-brand-primary mb-4" />
                <h3 className="font-bold text-lg mb-2">{t('missionTitle')}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{t('missionText')}</p>
              </div>
              <div className="p-6 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
                <Users className="w-8 h-8 text-brand-primary mb-4" />
                <h3 className="font-bold text-lg mb-2">{t('teamTitle')}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{t('teamText')}</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 30 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="p-8 bg-brand-dark text-white rounded-3xl shadow-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-primary/20 rounded-full -mr-16 -mt-16 blur-2xl group-hover:scale-150 transition-transform duration-700" />
              <Lightbulb className="w-12 h-12 text-brand-primary mb-6" />
              <h3 className="text-2xl font-bold mb-4">{t('visionTitle')}</h3>
              <p className="text-brand-light/80 leading-relaxed text-lg italic">
                {t('visionText')}
              </p>
            </div>

            <div className="p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
              <h3 className="text-2xl font-bold mb-8">{t('whyTitle')}</h3>
              <div className="space-y-6">
                {[t('why1'), t('why2'), t('why3')].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-full bg-brand-primary text-white flex items-center justify-center font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <p className="font-medium text-slate-700 dark:text-slate-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="text-center py-12 px-6 bg-slate-50 dark:bg-slate-900 rounded-[3rem] border border-brand-light dark:border-slate-800"
        >
          <span className="text-4xl md:text-5xl font-black text-brand-primary opacity-20 block mb-4">"</span>
          <h4 className="text-2xl md:text-3xl font-extrabold text-brand-dark dark:text-white italic max-w-2xl mx-auto">
            {t('aboutQuote')}
          </h4>
          <span className="text-4xl md:text-5xl font-black text-brand-primary opacity-20 block mt-4">"</span>
        </motion.div>
      </div>
    </section>
  );
};

const AISimulator = () => {
  const { t } = useAppContext();
  const [problem, setProblem] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<null | {
    cause: string;
    recommendation: string;
    risk: "Normal" | "Alerte" | "Critique" | "Warning" | "Critical";
    impact: string;
  }>(null);

  const handleDiagnose = async () => {
    if (!problem.trim()) return;
    setLoading(true);
    setResult(null);

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: `Analysez ce symptôme de maintenance industrielle et donnez un diagnostic en français : "${problem}"`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              cause: { type: Type.STRING },
              recommendation: { type: Type.STRING },
              risk: { type: Type.STRING, description: "Critique, Alerte ou Normal" },
              impact: { type: Type.STRING }
            },
            required: ["cause", "recommendation", "risk", "impact"]
          },
          systemInstruction: "Tu es un expert en maintenance industrielle prédictive. Analyse les symptômes techniques (vibrations, température, bruit, etc.) et propose un diagnostic structuré. Sois précis et technique."
        }
      });

      const data = JSON.parse(response.text);
      setResult(data);
    } catch (error) {
      console.error("Simulation error", error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk: string) => {
    const r = risk.toLowerCase();
    if (r.includes("critique") || r.includes("critical")) return "bg-red-100 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-900/50";
    if (r.includes("alerte") || r.includes("warning")) return "bg-yellow-100 text-yellow-600 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-900/50";
    return "bg-green-100 text-green-600 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-900/50";
  };

  return (
    <section className="py-24 bg-brand-dark dark:bg-slate-950 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(0,156,222,0.1),transparent_70%)]" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} className="text-4xl font-extrabold mb-4">{t('simulatorTitle')}</motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-brand-light/70 mb-12 max-w-2xl mx-auto">{t('simulatorSubtitle')}</motion.p>
        
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-[2rem] p-8 border border-white/10 shadow-2xl text-left">
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Description du problème (Ex: "Vibration excessive...")</label>
          <textarea 
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            className="w-full bg-brand-dark/50 border border-white/10 rounded-2xl p-6 text-white placeholder-slate-500 focus:ring-2 focus:ring-brand-primary outline-none min-h-[120px] transition-all"
            placeholder={t('simulatorPlaceholder')}
          />
          
          <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
            <div className="flex items-center space-x-2 text-slate-400 text-xs">
              <Info className="w-4 h-4" />
              <span>IA Gemini 3 Flash | Mode démo</span>
            </div>
            <button 
              onClick={handleDiagnose}
              disabled={loading || !problem.trim()}
              className="bg-brand-primary hover:bg-brand-primary/80 disabled:opacity-50 text-white px-8 py-4 rounded-xl font-bold flex items-center space-x-3 transition-all shadow-lg shadow-brand-primary/20"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
              <span>{t('simulatorBtn')}</span>
            </button>
          </div>

          <AnimatePresence>
            {result && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }} 
                animate={{ opacity: 1, height: 'auto' }} 
                exit={{ opacity: 0, height: 0 }}
                className="mt-12 pt-8 border-t border-white/10"
              >
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-xl font-bold">{t('simulatorResultTitle')}</h3>
                  <div className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border ${getRiskColor(result.risk)}`}>
                    Risque : {result.risk}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-6 bg-brand-dark/40 rounded-2xl border border-white/5">
                    <div className="flex items-center space-x-2 text-brand-primary mb-3">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em]">{t('simulatorCause')}</span>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-200">{result.cause}</p>
                  </div>
                  <div className="p-6 bg-brand-dark/40 rounded-2xl border border-white/5">
                    <div className="flex items-center space-x-2 text-green-400 mb-3">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em]">{t('simulatorRecom')}</span>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-200">{result.recommendation}</p>
                  </div>
                </div>

                <div className="mt-6 p-6 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-start space-x-4">
                  <Info className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-200 font-medium">
                      <span className="text-brand-primary font-bold">{t('simulatorImpact')} :</span> {result.impact}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-2">{t('simulatorImpactDesc')}</p>
                    <a href="#contact" className="inline-block mt-4 text-brand-primary text-sm font-bold hover:underline">
                      {t('simulatorDiscuss')}
                    </a>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const DashboardSection = () => {
  const { t } = useAppContext();
  
  // Data for the chart
  const [chartData, setChartData] = useState(() => Array.from({ length: 30 }, (_, i) => ({
    name: `Day ${i + 1}`,
    value: 85 + Math.random() * 10 + (i > 20 ? 3 : 0), // Slight upward trend
    prediction: 85 + Math.random() * 10
  })));

  return (
    <section id="dashboard" className="py-24 bg-slate-50 dark:bg-slate-900/50 overflow-hidden scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-brand-light dark:bg-brand-dark/30 text-brand-dark dark:text-brand-primary text-sm font-bold mb-6">
            <Eye className="w-4 h-4" />
            <span>{t('dashboardLive')}</span>
          </div>
          <h2 className="text-4xl font-bold mb-4">{t('dashboardTitle')}</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Une interface unifiée pour surveiller vos actifs, prédire les pannes et optimiser la production.
          </p>
        </div>

        {/* Pro Dashboard Container (Dark Themed to mimic the screenshot) */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-[#0b0e14] rounded-[2rem] border border-slate-800 shadow-2xl overflow-hidden min-h-[700px] flex text-slate-200 font-sans"
        >
          {/* Sidebar */}
          <div className="w-20 lg:w-64 bg-[#0F1219] border-r border-slate-800 flex flex-col p-4">
            <div className="flex items-center space-x-3 mb-10 px-2">
              <LogoIcon className="w-8 h-8 flex-shrink-0" />
              <span className="font-bold text-lg hidden lg:block tracking-tight text-white">SPI Flow</span>
            </div>
            
            <nav className="flex-1 space-y-2">
              {[
                { icon: LayoutDashboard, label: t('dashboardSideOverview'), active: true },
                { icon: Database, label: t('dashboardSideAssets') },
                { icon: Bell, label: t('dashboardSideAlerts') },
                { icon: FileText, label: t('dashboardSideReports') },
                { icon: Settings, label: t('dashboardSideSettings') },
              ].map((item, i) => (
                <div key={i} className={`flex items-center space-x-3 p-3 rounded-xl cursor-pointer transition-colors ${item.active ? 'bg-brand-primary/20 text-brand-primary' : 'hover:bg-slate-800 text-slate-400 hover:text-white'}`}>
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium hidden lg:block">{item.label}</span>
                </div>
              ))}
            </nav>

            <div className="mt-auto pt-6 border-t border-slate-800">
               <div className="flex items-center space-x-3 p-3 rounded-xl cursor-pointer hover:bg-slate-800 text-slate-400 hover:text-red-400 transition-colors">
                  <LogOut className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium hidden lg:block">Déconnexion</span>
               </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col relative overflow-hidden">
             {/* Glowing Effect Background */}
             <div className="absolute top-0 left-0 w-full h-[500px] bg-brand-primary/5 rounded-full blur-[100px] pointer-events-none -translate-y-1/2"></div>

             {/* Header */}
             <header className="h-20 border-b border-slate-800 flex items-center justify-between px-8 z-10">
                <div className="flex items-center space-x-2 text-sm text-slate-400">
                   <div className="p-1.5 bg-brand-primary/20 rounded-lg"><ChevronLeftIcon className="w-4 h-4 text-brand-primary" /></div>
                   <span>{t('dashboardBreadcrumb')}</span>
                </div>
                <div className="flex items-center space-x-4">
                   <div className="relative">
                      <div className="w-2 h-2 bg-red-500 rounded-full absolute top-0 right-0"></div>
                      <Bell className="w-5 h-5 text-slate-400 cursor-pointer hover:text-white" />
                   </div>
                   <div className="w-8 h-8 rounded-full bg-slate-700 border border-slate-600"></div>
                </div>
             </header>

             <div className="flex-1 p-8 overflow-y-auto z-10 custom-scrollbar">
                
                {/* Account/Asset Info Card */}
                <div className="bg-[#151921] rounded-2xl p-6 border border-slate-800 mb-8">
                   <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                         <h3 className="text-white font-bold text-lg mb-1">{t('dashboardStatusTitle')}</h3>
                         <p className="text-slate-400 text-sm font-mono">{t('dashboardStatusSub')}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                         <span className="px-3 py-1 bg-green-500/10 text-green-400 border border-green-500/20 rounded-full text-xs font-bold uppercase tracking-wide">En Production</span>
                      </div>
                   </div>
                </div>

                {/* Main Metrics - Profit/Loss Style */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                   {/* Main Big Metric */}
                   <div className="col-span-1 lg:col-span-2 bg-[#151921] rounded-2xl p-8 border border-slate-800 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-brand-primary/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
                      <h4 className="text-slate-400 text-sm font-medium mb-2">{t('dashboardAssetHealth')}</h4>
                      <div className="flex items-end space-x-4">
                         <span className="text-5xl font-bold text-white tracking-tight">98.2%</span>
                         <span className="mb-2 px-2 py-1 bg-brand-primary/20 text-brand-primary rounded text-xs font-bold">+2.4%</span>
                      </div>
                      <div className="mt-6 w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                         <div className="h-full bg-brand-primary w-[98.2%] rounded-full"></div>
                      </div>
                   </div>

                   {/* Secondary Metric */}
                   <div className="bg-[#151921] rounded-2xl p-6 border border-slate-800 flex flex-col justify-center">
                      <div className="flex items-center space-x-3 mb-4">
                         <div className="p-2 bg-purple-500/20 rounded-lg"><Zap className="w-5 h-5 text-purple-400" /></div>
                         <span className="text-slate-400 text-sm font-bold">{t('dashboardPredictions')}</span>
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">12</div>
                      <p className="text-xs text-slate-500">Arrêts critiques évités ce mois</p>
                   </div>
                   
                   {/* Tertiary Metric */}
                   <div className="bg-[#151921] rounded-2xl p-6 border border-slate-800 flex flex-col justify-center lg:col-start-3">
                      <div className="flex items-center space-x-3 mb-4">
                         <div className="p-2 bg-blue-500/20 rounded-lg"><Activity className="w-5 h-5 text-blue-400" /></div>
                         <span className="text-slate-400 text-sm font-bold">{t('dashboardUptime')}</span>
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">99.9%</div>
                      <p className="text-xs text-slate-500">Disponibilité sur 30 jours</p>
                   </div>
                </div>

                {/* Chart Section */}
                <div className="bg-[#151921] rounded-2xl p-6 border border-slate-800 min-h-[400px]">
                   <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center space-x-2">
                         <div className="w-2 h-2 rounded-full bg-brand-primary"></div>
                         <h3 className="text-white font-bold">{t('dashboardOverview')}</h3>
                      </div>
                      <div className="flex space-x-2 text-xs">
                         <button className="px-3 py-1 bg-slate-800 text-slate-300 rounded hover:bg-slate-700">1H</button>
                         <button className="px-3 py-1 bg-slate-800 text-slate-300 rounded hover:bg-slate-700">24H</button>
                         <button className="px-3 py-1 bg-brand-primary text-white rounded font-bold">7J</button>
                      </div>
                   </div>

                   <div className="h-[300px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                         <AreaChart data={chartData}>
                            <defs>
                               <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#009CDE" stopOpacity={0.3}/>
                                  <stop offset="95%" stopColor="#009CDE" stopOpacity={0}/>
                               </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" />
                            <XAxis hide />
                            <YAxis hide domain={['dataMin - 5', 'dataMax + 5']} />
                            <Tooltip 
                               contentStyle={{ backgroundColor: '#0F1219', border: '1px solid #1e293b', borderRadius: '8px', color: '#fff' }}
                               itemStyle={{ color: '#009CDE' }}
                            />
                            <Area 
                               type="monotone" 
                               dataKey="value" 
                               stroke="#009CDE" 
                               strokeWidth={3} 
                               fillOpacity={1} 
                               fill="url(#colorValue)" 
                            />
                         </AreaChart>
                      </ResponsiveContainer>
                   </div>
                </div>
                
                {/* Detail Stats Table Mockup */}
                <div className="mt-8">
                    <h3 className="text-white font-bold mb-4">Détails récents</h3>
                    <div className="bg-[#151921] rounded-2xl border border-slate-800 overflow-hidden">
                        {[1, 2, 3].map((item, i) => (
                            <div key={i} className="flex items-center justify-between p-4 border-b border-slate-800 last:border-none hover:bg-slate-800/50 transition-colors">
                                <div className="flex items-center space-x-4">
                                    <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-green-500' : 'bg-slate-500'}`}></div>
                                    <span className="text-sm text-slate-300">Analyse Vibratoire #{2049 + i}</span>
                                </div>
                                <div className="text-sm font-mono text-slate-500">24/10/2024 - 14:3{i}</div>
                                <div className="text-sm font-bold text-white">Normal</div>
                            </div>
                        ))}
                    </div>
                </div>

             </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Helper for the chevron icon needed in the dashboard header
const ChevronLeftIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m15 18-6-6 6-6"/></svg>
);

interface SolutionCardProps { 
  title: string; 
  desc: string; 
  icon: any; 
  delay: number;
  onClick: () => void;
}
const SolutionCard: React.FC<SolutionCardProps> = ({ title, desc, icon: Icon, delay, onClick }) => (
  <motion.div 
    initial={{ opacity: 0, y: 30 }} 
    whileInView={{ opacity: 1, y: 0 }} 
    viewport={{ once: true }} 
    transition={{ delay }} 
    whileHover={{ y: -10, scale: 1.02 }} 
    className="group p-8 bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none hover:border-brand-primary transition-all duration-300"
  >
    <div className="w-14 h-14 bg-brand-light dark:bg-brand-dark/30 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-brand-primary group-hover:text-white transition-colors duration-300 text-brand-primary"><Icon className="w-7 h-7" /></div>
    <h3 className="text-xl font-bold mb-4">{title}</h3>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">{desc}</p>
    <div onClick={onClick} className="flex items-center text-brand-primary font-bold text-sm cursor-pointer hover:underline">
      En savoir plus <ChevronRight className="ml-1 w-4 h-4" />
    </div>
  </motion.div>
);

const Solutions = () => {
  const { t } = useAppContext();
  const [selectedSolution, setSelectedSolution] = useState<null | { title: string; desc: string; details: string; icon: any }>(null);

  const solutions = [
    { title: t('chemTitle'), desc: t('chemDesc'), details: t('chemDetails'), icon: Pipette },
    { title: t('energyTitle'), desc: t('energyDesc'), details: t('energyDetails'), icon: Flame },
    { title: t('metalTitle'), desc: t('metalDesc'), details: t('metalDetails'), icon: Factory },
    { title: t('petroTitle'), desc: t('petroDesc'), details: t('petroDetails'), icon: Truck },
    { title: t('agroTitle'), desc: t('agroDesc'), details: t('agroDetails'), icon: Settings },
    { title: t('autoTitle'), desc: t('autoDesc'), details: t('autoDetails'), icon: Cpu },
  ];

  return (
    <section id="solutions" className="py-24 scroll-mt-20 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-4xl font-extrabold mb-6">{t('solutionsTitle')}</motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-lg text-slate-600 dark:text-slate-400">{t('solutionsSubtitle')}</motion.p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {solutions.map((s, i) => ( 
            <SolutionCard 
              key={i} 
              title={s.title} 
              desc={s.desc} 
              icon={s.icon} 
              delay={i * 0.1} 
              onClick={() => setSelectedSolution(s)}
            /> 
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedSolution && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            onClick={() => setSelectedSolution(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }} 
              animate={{ opacity: 1, scale: 1, y: 0 }} 
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-slate-900 rounded-2xl p-8 max-w-lg w-full relative shadow-2xl border border-slate-200 dark:border-slate-800"
            >
              <button onClick={() => setSelectedSolution(null)} className="absolute top-4 right-4 p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
              
              <div className="w-16 h-16 bg-brand-light dark:bg-brand-dark/30 rounded-2xl flex items-center justify-center mb-6 text-brand-primary">
                <selectedSolution.icon className="w-8 h-8" />
              </div>
              
              <h3 className="text-2xl font-bold mb-4">{selectedSolution.title}</h3>
              <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-6 leading-relaxed">
                {selectedSolution.desc}
              </p>
              
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {selectedSolution.details}
                </p>
              </div>

              <button 
                onClick={() => setSelectedSolution(null)}
                className="w-full mt-8 bg-brand-primary text-white font-bold py-3 rounded-xl hover:bg-brand-dark transition-colors"
              >
                Fermer
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const Missions = () => {
  const { t } = useAppContext();
  const missions = [
    { title: t('m1Title'), desc: t('m1Desc'), icon: Activity },
    { title: t('m2Title'), desc: t('m2Desc'), icon: Gauge },
    { title: t('m3Title'), desc: t('m3Desc'), icon: Zap },
    { title: t('m4Title'), desc: t('m4Desc'), icon: ShieldCheck },
    { title: t('m5Title'), desc: t('m5Desc'), icon: Cpu },
    { title: t('m6Title'), desc: t('m6Desc'), icon: Battery },
  ];
  return (
    <section id="missions" className="py-24 bg-brand-dark text-white overflow-hidden relative">
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl -mr-48 -mt-48" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl -ml-48 -mb-48" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-20"><h2 className="text-4xl font-bold mb-4">{t('missionsTitle')}</h2><div className="w-20 h-1 bg-brand-primary rounded-full" /></div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
          {missions.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex space-x-6">
              <div className="flex-shrink-0"><div className="w-12 h-12 rounded-xl bg-brand-primary/30 flex items-center justify-center border border-brand-primary/30"><m.icon className="w-6 h-6 text-brand-primary" /></div></div>
              <div><h3 className="text-xl font-bold mb-3">{m.title}</h3><p className="text-brand-light/70 leading-relaxed text-sm">{m.desc}</p></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Partners = () => {
  const { t } = useAppContext();
  const partners = ["BPI FRANCE", "FRENCH TECH", "REGION AUVERGNE-RHONE-ALPES", "LYON STARTUP", "GROUPE ORTEC"];
  return (
    <section className="py-20 bg-white dark:bg-slate-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 mb-12"><h3 className="text-center text-sm font-bold text-brand-dark dark:text-brand-primary uppercase tracking-[0.3em]">{t('partnersTitle')}</h3></div>
      <div className="relative flex overflow-x-hidden group">
        <motion.div 
          className="flex py-12 whitespace-nowrap animate-marquee"
          animate={{ x: [0, -1920] }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        >
          {[...partners, ...partners].map((p, i) => (
            <span key={i} className="mx-12 text-3xl font-black text-slate-300 dark:text-slate-800 hover:text-brand-primary transition-colors cursor-default whitespace-nowrap">{p}</span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Pricing = () => {
  const { t } = useAppContext();
  
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const plans = [
    { 
      name: t('planStarter'), desc: t('planStarterDesc'), icon: <Settings/>, 
      features: [t('planStarterF1'), t('planStarterF2'), t('planStarterF3'), t('planStarterF4'), t('planStarterF5')] 
    },
    { 
      name: t('planPro'), desc: t('planProDesc'), icon: <Zap/>, featured: true, 
      features: [t('planProF1'), t('planProF2'), t('planProF3'), t('planProF4'), t('planProF5'), t('planProF6')] 
    },
    { 
      name: t('planEnterprise'), desc: t('planEnterpriseDesc'), icon: <ShieldCheck/>, 
      features: [t('planEnterpriseF1'), t('planEnterpriseF2'), t('planEnterpriseF3'), t('planEnterpriseF4'), t('planEnterpriseF5'), t('planEnterpriseF6'), t('planEnterpriseF7')] 
    },
  ];

  return (
    <section id="pricing" className="py-24 bg-slate-50 dark:bg-slate-900/50 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center mb-16">{t('pricingTitle')}</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, i) => (
            <motion.div key={i} whileHover={{ y: -10 }} className={`p-10 rounded-3xl flex flex-col ${plan.featured ? 'bg-brand-dark text-white shadow-2xl scale-105 z-10' : 'bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800'}`}>
              {plan.featured && <div className="text-center font-bold text-xs uppercase tracking-widest mb-4">Recommended</div>}
              <h3 className="text-2xl font-bold mb-4">{plan.name}</h3>
              <p className={`mb-12 text-sm ${plan.featured ? 'text-brand-light' : 'text-slate-500'}`}>{plan.desc}</p>
              <div className="mt-auto space-y-4 mb-10">
                {plan.features.map((f, idx) => (
                  <div key={idx} className="flex items-center space-x-3 text-sm"><CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${plan.featured ? 'text-white' : 'text-brand-primary'}`} /><span>{f}</span></div>
                ))}
              </div>
              <a 
                href="#contact" 
                onClick={scrollToContact}
                className={`text-center py-4 rounded-xl font-bold transition-all ${plan.featured ? 'bg-white text-brand-dark hover:bg-slate-100' : 'bg-brand-dark text-white hover:bg-brand-primary'}`}
              >
                {t('quote')}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Fixed FAQItem using React.FC to properly handle props including 'key'
const FAQItem: React.FC<{ question: string, answer: string, isOpen: boolean, toggle: () => void }> = ({ question, answer, isOpen, toggle }) => (
  <div className="border-b border-slate-200 dark:border-slate-800 last:border-none">
    <button onClick={toggle} className="w-full py-6 flex items-center justify-between text-left hover:text-brand-primary transition-colors">
      <span className="text-lg font-bold pr-8">{question}</span>
      <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <AnimatePresence>{isOpen && (
      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
        <p className="pb-6 text-slate-600 dark:text-slate-400 leading-relaxed text-sm">{answer}</p>
      </motion.div>
    )}</AnimatePresence>
  </div>
);

const FAQ = () => {
  const { t } = useAppContext();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [ 
    { q: t('faq1Q'), a: t('faq1A') }, 
    { q: t('faq2Q'), a: t('faq2A') }, 
    { q: t('faq3Q'), a: t('faq3A') }, 
    { q: t('faq4Q'), a: t('faq4A') }, 
    { q: t('faq5Q'), a: t('faq5A') }, 
    { q: t('faq6Q'), a: t('faq6A') } 
  ];
  return (
    <section id="faq" className="py-24 bg-white dark:bg-slate-950 scroll-mt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center mb-16">{t('faqTitle')}</h2>
        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-3xl p-8 border border-slate-100 dark:border-slate-800 shadow-sm">
          {faqs.map((faq, i) => ( <FAQItem key={i} question={faq.q} answer={faq.a} isOpen={openIndex === i} toggle={() => setOpenIndex(openIndex === i ? null : i)} /> ))}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const { t } = useAppContext();
  const [status, setStatus] = useState<'IDLE' | 'SUBMITTING' | 'SUCCESS' | 'ERROR'>('IDLE');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    setStatus('SUBMITTING');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setStatus('SUCCESS');
        form.reset();
      } else {
        setStatus('ERROR');
      }
    } catch (error) {
      setStatus('ERROR');
    }
  };

  return (
    <section id="contact" className="py-24 scroll-mt-20 bg-slate-50 dark:bg-slate-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-bold mb-8">{t('contactTitle')}</h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-12">Discutons de votre projet de maintenance prédictive et voyons comment l'IA peut booster vos performances industrielles.</p>
            <div className="space-y-8">
              {[ 
                { icon: <Mail className="text-brand-primary"/>, label: "Email", val: "contact@safe-predictive.com" }, 
                { icon: <Phone className="text-brand-primary"/>, label: "Téléphone", val: "+33 6 69 01 09 47" },
                { icon: <Linkedin className="text-brand-primary"/>, label: "LinkedIn", val: "safe-predictive-innovation" }, 
                { icon: <MapPin className="text-brand-primary"/>, label: "Siège", val: "60 rue François 1er, 75008 Paris" } 
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-4"><div className="p-3 bg-brand-light dark:bg-brand-dark/30 rounded-xl">{item.icon}</div><div><div className="text-sm font-bold text-slate-500 uppercase tracking-wider">{item.label}</div><div className="text-lg font-medium">{item.val}</div></div></div>
              ))}
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white dark:bg-slate-800 p-10 rounded-3xl shadow-2xl border border-slate-100 dark:border-slate-800 min-h-[500px] flex flex-col justify-center">
            <AnimatePresence mode="wait">
              {status === 'SUCCESS' ? (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="text-center"
                >
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{t('contactSuccess')}</h3>
                  <button 
                    onClick={() => setStatus('IDLE')}
                    className="text-brand-primary font-bold hover:underline"
                  >
                    {t('contactNew')}
                  </button>
                </motion.div>
              ) : (
                <motion.form 
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  action="https://formspree.io/f/manrdglr" 
                  method="POST" 
                  className="space-y-6"
                >
                  {status === 'ERROR' && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm font-bold rounded-xl border border-red-100 dark:border-red-900/50">
                      {t('contactError')}
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold mb-2 uppercase tracking-wide opacity-70">{t('placeholderName')}</label>
                      <input type="text" name="name" className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border-none focus:ring-2 focus:ring-brand-primary outline-none" required />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2 uppercase tracking-wide opacity-70">Entreprise</label>
                      <input type="text" name="company" className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border-none focus:ring-2 focus:ring-brand-primary outline-none" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 uppercase tracking-wide opacity-70">{t('placeholderEmail')}</label>
                    <input type="email" name="email" className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border-none focus:ring-2 focus:ring-brand-primary outline-none" required />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 uppercase tracking-wide opacity-70">{t('placeholderMsg')}</label>
                    <textarea name="message" rows={4} className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border-none focus:ring-2 focus:ring-brand-primary outline-none resize-none" required></textarea>
                  </div>
                  <button 
                    type="submit" 
                    disabled={status === 'SUBMITTING'}
                    className="w-full bg-brand-dark text-white font-bold py-5 rounded-xl hover:bg-brand-primary transition-all shadow-lg shadow-brand-dark/20 flex items-center justify-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {status === 'SUBMITTING' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>{t('contactSending')}</span>
                      </>
                    ) : (
                      <>
                        <span>{t('contactSend')}</span>
                        <ChevronRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const { t } = useAppContext();
  
  const scrollToPricing = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById('pricing');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <main>
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-slate-50/50 dark:bg-slate-950/50 z-[-1]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-4xl mx-auto mb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-brand-light dark:bg-brand-dark/30 text-brand-dark dark:text-brand-primary text-sm font-bold mb-8"><CheckCircle2 className="w-4 h-4" /><span>Innovation in Predictive Maintenance</span></motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-5xl md:text-7xl font-extrabold mb-8 tracking-tighter text-brand-dark dark:text-white">{t('heroTitle')}</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl text-slate-600 dark:text-slate-400 mb-10 leading-relaxed">{t('heroSubtitle')}</motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a 
              href="#pricing" 
              onClick={scrollToPricing}
              className="w-full sm:w-auto bg-brand-dark text-white px-8 py-5 rounded-2xl font-bold text-lg hover:bg-brand-primary transition-all shadow-xl shadow-brand-dark/20"
            >
              {t('heroCTA')}
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }} className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-16">
            {[ { label: t('heroStats1'), value: "-45%", color: "text-brand-primary" }, { label: t('heroStats2'), value: "300k€", color: "text-blue-500" }, { label: t('heroStats3'), value: "< 6m", color: "text-purple-500" }, { label: t('heroStatsTarget'), value: "+1k", color: "text-orange-500" } ].map((stat, i) => (
              <div key={i} className="bg-white dark:bg-slate-900 p-6 rounded-3xl shadow-lg border border-slate-100 dark:border-slate-800"><div className={`text-3xl font-black mb-1 ${stat.color}`}>{stat.value}</div><div className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">{stat.label}</div></div>
            ))}
          </motion.div>
        </div>
      </section>
      
      <AboutUs />
      <Solutions />
      <DashboardSection />
      <AISimulator />
      <Partners />
      <Missions />
      
      <section id="team" className="py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-20">
          <h2 className="text-4xl font-bold mb-4">{t('navTeam')}</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">Expertise métier et innovation technologique.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto px-4">
          <motion.div whileHover={{ y: -10 }} className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-xl flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
            <TeamAvatar />
            <div>
              <h3 className="text-2xl font-bold">{t('founder1Name')}</h3>
              <div className="text-brand-primary font-bold mb-4">{t('founder1Role')}</div>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">{t('founder1Bio')}</p>
            </div>
          </motion.div>
          <motion.div whileHover={{ y: -10 }} className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-xl flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
            <TeamAvatar />
            <div>
              <h3 className="text-2xl font-bold">{t('founder2Name')}</h3>
              <div className="text-brand-primary font-bold mb-4">{t('founder2Role')}</div>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">{t('founder2Bio')}</p>
            </div>
          </motion.div>
        </div>
      </section>
      <Pricing /><FAQ /><Contact />
    </main>
  );
};

// Fixed LegalPageWrapper using React.FC to properly handle children
const LegalPageWrapper: React.FC<{ title: string, children: React.ReactNode }> = ({ title, children }) => (
  <div className="pt-32 pb-24 bg-slate-50 dark:bg-slate-950 min-h-screen">
    <div className="max-w-4xl mx-auto px-4">
      <Link to="/" className="inline-flex items-center text-brand-primary font-bold mb-8 hover:translate-x-[-4px] transition-transform">
        <ArrowLeft className="w-5 h-5 mr-2" /> Retour à l'accueil
      </Link>
      <h1 className="text-4xl font-extrabold text-center text-brand-dark dark:text-white mb-16">{title}</h1>
      <div className="space-y-6">
        {children}
      </div>
    </div>
  </div>
);

// Fixed LegalSection using React.FC to properly handle children
const LegalSection: React.FC<{ title: string, children: React.ReactNode }> = ({ title, children }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm"
  >
    <h2 className="text-xl font-bold text-brand-primary mb-4">{title}</h2>
    <div className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">
      {children}
    </div>
  </motion.div>
);

const LegalMentions = () => (
  <LegalPageWrapper title="Mentions Légales">
    <LegalSection title="Éditeur du site">
      <p><strong>Safe Predictive Innovation</strong></p>
      <p>Société immatriculée (SIREN : 995 189 248)</p>
      <p>Siège social : 60 rue François 1er, 75008 Paris</p>
      <p>Lyon, France</p>
      <p>Email : contact@safe-predictive.com</p>
      <p>Téléphone : +33 6 69 01 09 47</p>
    </LegalSection>
    <LegalSection title="Hébergement">
      <p>Le site est hébergé en France, en conformité avec les réglementations européennes, garantissant un niveau élevé de protection des données et de disponibilité.</p>
    </LegalSection>
    <LegalSection title="Propriété intellectuelle">
      <p>L'ensemble du contenu de ce site (textes, images, vidéos, logos, icônes) est la propriété exclusive de Safe Predictive Innovation et est protégé par le droit d'auteur. Toute reproduction, représentation, modification ou publication, même partielle, est formellement interdite sans autorisation écrite préalable.</p>
    </LegalSection>
    <LegalSection title="Données personnelles">
      <p>Les données collectées via le formulaire de contact (nom, email, entreprise) sont utilisées exclusivement pour répondre à vos demandes commerciales ou techniques. Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression de vos données personnelles.</p>
    </LegalSection>
    <LegalSection title="Cookies">
      <p>Ce site utilise des cookies essentiels pour améliorer votre expérience de navigation et analyser anonymement le trafic. Vous pouvez configurer vos préférences via les réglages de votre navigateur.</p>
    </LegalSection>
  </LegalPageWrapper>
);

const PrivacyPolicy = () => (
  <LegalPageWrapper title="Politique de confidentialité">
    <LegalSection title="Collecte des données">
      <p>Nous collectons uniquement les données strictement nécessaires pour répondre à vos demandes transmises via notre formulaire de contact : <strong>nom, email, entreprise et message</strong>. Aucun profilage automatisé n'est effectué.</p>
    </LegalSection>
    <LegalSection title="Utilisation des données">
      <p>Vos données sont traitées avec la plus grande confidentialité. Elles sont utilisées exclusivement pour répondre à vos demandes d'information et de démonstration. Nous nous engageons à <strong>ne jamais partager ou vendre</strong> vos données à des tiers.</p>
    </LegalSection>
    <LegalSection title="Conservation des données">
      <p>Vos données personnelles sont conservées pendant une durée maximale de 3 ans après votre dernier contact avec nos services, conformément aux préconisations de la CNIL et aux obligations légales en vigueur.</p>
    </LegalSection>
    <LegalSection title="Vos droits">
      <p>En vertu du RGPD, vous disposez des droits suivants concernant vos informations :</p>
      <ul className="list-disc list-inside mt-2 space-y-1">
        <li>Droit d'accès et de rectification</li>
        <li>Droit à l'effacement (droit à l'oubli)</li>
        <li>Droit à la portabilité des données</li>
        <li>Droit d'opposition ou de limitation du traitement</li>
      </ul>
      <p className="mt-4">Pour exercer ces droits, contactez notre délégué à la protection des données : <strong>dpo@safe-predictive.com</strong></p>
    </LegalSection>
    <LegalSection title="Sécurité">
      <p>Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées (chiffrement SSL, pare-feu industriels) pour protéger vos données contre tout accès non autorisé, modification, divulgation ou destruction.</p>
    </LegalSection>
  </LegalPageWrapper>
);

const Footer = () => {
  const { t } = useAppContext();
  return (
    <footer className="bg-white dark:bg-slate-950 pt-20 pb-10 border-t border-slate-100 dark:border-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <LogoIcon className="w-10 h-10" />
              <div className="flex flex-col">
                <span className="font-extrabold text-lg tracking-tighter text-brand-dark dark:text-white leading-none">SAFE PREDICTIVE</span>
                <span className="text-[10px] uppercase tracking-widest text-brand-primary font-bold">Innovation</span>
              </div>
            </Link>
            <p className="text-slate-500 dark:text-slate-400 max-w-sm mb-8 text-sm">Expertise IA & Maintenance prédictive pour l'industrie lourde. Digitalisation industrielle intelligente.</p>
            <div className="flex space-x-4">
              <a href="https://linkedin.com/company/safe-predictive-innovation" target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-50 dark:bg-slate-900 rounded-full hover:bg-brand-primary hover:text-white transition-all"><Linkedin className="w-5 h-5" /></a>
              <a href="mailto:contact@safe-predictive.com" className="p-3 bg-slate-50 dark:bg-slate-900 rounded-full hover:bg-brand-primary hover:text-white transition-all"><Mail className="w-5 h-5" /></a>
              <a href="tel:+33669010947" className="p-3 bg-slate-50 dark:bg-slate-900 rounded-full hover:bg-brand-primary hover:text-white transition-all"><Phone className="w-5 h-5" /></a>
            </div>
          </div>
          <div><h4 className="font-bold mb-6 uppercase text-xs tracking-widest text-slate-400">Navigation</h4><ul className="space-y-4 text-sm"><li><Link to="/" className="hover:text-brand-primary transition-colors">Accueil</Link></li><li><Link to="/#about" className="hover:text-brand-primary transition-colors">À propos</Link></li><li><Link to="/#solutions" className="hover:text-brand-primary transition-colors">Solutions</Link></li><li><Link to="/#dashboard" className="hover:text-brand-primary transition-colors">Dashboard</Link></li><li><Link to="/#pricing" className="hover:text-brand-primary transition-colors">Tarifs</Link></li></ul></div>
          <div><h4 className="font-bold mb-6 uppercase text-xs tracking-widest text-slate-400">Légal</h4><ul className="space-y-4 text-sm"><li><Link to="/mentions-legales" className="hover:text-brand-primary transition-colors">{t('legalLink')}</Link></li><li><Link to="/confidentialite" className="hover:text-brand-primary transition-colors">{t('privacyLink')}</Link></li></ul></div>
        </div>
        <div className="pt-8 border-t border-slate-100 dark:border-slate-900 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500"><p>© {new Date().getFullYear()} Safe Predictive Innovation. {t('footerRights')}</p><div className="flex items-center space-x-6 mt-4 md:mt-0"><span className="flex items-center space-x-1"><MapPin className="w-4 h-4" /> <span>60 rue François 1er, 75008 Paris</span></span></div></div>
      </div>
    </footer>
  );
};

const App = () => {
  const [lang, setLang] = useState<Language>(() => (localStorage.getItem('spi_lang') as Language) || 'fr');
  const [isDark, setIsDark] = useState(() => localStorage.getItem('spi_dark') === 'true');
  
  useEffect(() => { 
    localStorage.setItem('spi_lang', lang); 
  }, [lang]);

  useEffect(() => { 
    localStorage.setItem('spi_dark', isDark.toString()); 
    const root = window.document.documentElement;
    if (isDark) { 
      root.classList.add('dark'); 
    } else { 
      root.classList.remove('dark'); 
    } 
  }, [isDark]);

  const t = (key: string) => translations[lang][key] || key;
  
  return (
    <AppContext.Provider value={{ lang, setLang, isDark, setIsDark, t }}>
      <HashRouter>
        <ScrollToTop />
        <div className="min-h-screen bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100 font-sans">
          <Navbar />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/mentions-legales" element={<LegalMentions />} />
            <Route path="/confidentialite" element={<PrivacyPolicy />} />
          </Routes>
          <Footer />
        </div>
      </HashRouter>
    </AppContext.Provider>
  );
};
export default App;
